/**
 * Priority Control Panel Component
 * 
 * Allows adjusting priorities (sequence, on/off) for improvement and operational items.
 * Used in domain detail sections.
 */

import React, { useState, useEffect } from 'react';
import { Table, Switch, Button, Space, Typography, Tag, Select } from 'antd';
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import styles from './PriorityControlPanel.module.css';

const { Text } = Typography;

export interface Priority {
  id: string;
  name: string;
  sequence: number;
  enabled: boolean;
  type: 'improvement' | 'operational';
  domain_artifact?: string;
  resource_name?: string;
  start_date?: string;
  end_date?: string;
  duration?: string;
  remaining_duration?: string;
}

export interface PriorityControlPanelProps {
  /** Domain ID */
  domainId: string;
  /** Priorities list */
  priorities: Priority[];
  /** Callback when priorities change */
  onPrioritiesChange?: (priorities: Priority[]) => void;
  /** Whether controls are disabled */
  disabled?: boolean;
  /** Whether to use compact layout */
  compact?: boolean;
  /** Optional domain filter (Strategy, Finance, Operations, Revenue, Resource) */
  domainFilter?: string | null;
  /** Callback when domain filter changes */
  onDomainFilterChange?: (domain: string | null) => void;
}

const DOMAIN_OPTIONS = [
  { value: '', label: 'All domains' },
  { value: 'Strategy', label: 'Strategy' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Operations', label: 'Operations' },
  { value: 'Revenue', label: 'Revenue' },
  { value: 'Resource', label: 'Resource' },
];

export const PriorityControlPanel: React.FC<PriorityControlPanelProps> = ({
  domainId,
  priorities: initialPriorities,
  onPrioritiesChange,
  disabled = false,
  compact = false,
  domainFilter = null,
  onDomainFilterChange,
}) => {
  const [priorities, setPriorities] = useState<Priority[]>(initialPriorities);
  useEffect(() => {
    setPriorities(initialPriorities);
  }, [initialPriorities]);
  const filteredPriorities = domainFilter
    ? priorities.filter((p) => p.domain_artifact === domainFilter)
    : priorities;

  const handleSequenceChange = (id: string, direction: 'up' | 'down') => {
    const newPriorities = [...priorities];
    const index = newPriorities.findIndex(p => p.id === id);
    if (index === -1) return;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newPriorities.length) return;

    // Swap sequence numbers
    const temp = newPriorities[index].sequence;
    newPriorities[index].sequence = newPriorities[targetIndex].sequence;
    newPriorities[targetIndex].sequence = temp;

    // Sort by sequence
    newPriorities.sort((a, b) => a.sequence - b.sequence);
    setPriorities(newPriorities);
    onPrioritiesChange?.(newPriorities);
  };

  const handleToggle = (id: string) => {
    const newPriorities = priorities.map(p =>
      p.id === id ? { ...p, enabled: !p.enabled } : p
    );
    setPriorities(newPriorities);
    onPrioritiesChange?.(newPriorities);
  };

  const columns = [
    {
      title: 'Sequence',
      key: 'sequence',
      width: 100,
      render: (_: any, record: Priority, index: number) => (
        <Space>
          <Button
            type="text"
            size="small"
            icon={<ArrowUpOutlined />}
            onClick={() => handleSequenceChange(record.id, 'up')}
            disabled={disabled || index === 0}
            className={styles.sequenceButton}
          />
          <Text strong>{record.sequence}</Text>
          <Button
            type="text"
            size="small"
            icon={<ArrowDownOutlined />}
            onClick={() => handleSequenceChange(record.id, 'down')}
            disabled={disabled || index === priorities.length - 1}
            className={styles.sequenceButton}
          />
        </Space>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Priority) => (
        <Space direction="vertical" size={0}>
          <Space>
            <Text>{name}</Text>
            <Tag color={record.type === 'improvement' ? 'blue' : 'green'}>
              {record.type}
            </Tag>
            {record.domain_artifact && (
              <Tag>{record.domain_artifact}</Tag>
            )}
          </Space>
          {(record.resource_name || record.start_date || record.duration) && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.resource_name && `Assignee: ${record.resource_name}`}
              {record.start_date && record.end_date && ` | ${record.start_date} – ${record.end_date}`}
              {record.duration && ` | ${record.duration}`}
              {record.remaining_duration && ` (${record.remaining_duration} remaining)`}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'enabled',
      width: 100,
      render: (_: any, record: Priority) => (
        <Switch
          checked={record.enabled}
          onChange={() => handleToggle(record.id)}
          disabled={disabled}
          checkedChildren={<PlayCircleOutlined />}
          unCheckedChildren={<PauseCircleOutlined />}
        />
      ),
    },
  ];

  return (
    <div className={`${styles.panel} ${compact ? styles.compact : ''}`}>
      {!compact && (
        <div className={styles.header}>
          <Text strong>Priority Management</Text>
          <Text type="secondary" className={styles.subtitle}>
            Adjust sequence and enable/disable priorities
          </Text>
        </div>
      )}
      {domainId === 'business' && onDomainFilterChange && (
        <div style={{ marginBottom: 12 }}>
          <Select
            placeholder="Filter by domain"
            allowClear
            value={domainFilter || undefined}
            onChange={(v) => onDomainFilterChange(v || null)}
            options={DOMAIN_OPTIONS}
            style={{ width: 180 }}
          />
        </div>
      )}
      <Table
        dataSource={filteredPriorities}
        columns={columns}
        rowKey="id"
        pagination={false}
        className={styles.table}
        size={compact ? 'small' : 'middle'}
      />
    </div>
  );
};

export default PriorityControlPanel;
